import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DatePicker,
} from "web-design-system";
import { useCallback, useEffect, useMemo } from "react";
import { format, parse } from "date-fns";
import { usePlanStartDate } from "creso-core/hooks";
import { WEEK_DAYS } from "creso-core/constants";
import isEmpty from "lodash/isEmpty";

const DEFAULT_DAYS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22,
  23, 24, 25, 26, 27, 28,
];

export function PlanStartDate({
  planType,
  frequency,
  firstOrderToday,
  placeholder,
  onChange,
  value,
  days = DEFAULT_DAYS,
}: {
  planType: "XSIP" | "SWP" | "STP";
  frequency: "DAILY" | "WEEKLY" | "MONTHLY";
  firstOrderToday: boolean;
  placeholder: string;
  onChange: (value: string) => void;
  value: string;
  days?: number[];
}) {
  const dayNo = useMemo(() => {
    return value
      ? parse(value, "yyyy-MM-dd", new Date()).getDate().toString()
      : "";
  }, [value]);

  const [minDate, getStartDate] = usePlanStartDate({
    planType,
    frequency,
    firstOrderToday,
  });

  /**
   * `handleChange` would always find the closest start dates possible.
   * Parses the input value and computed new start date based on frequency,
   * even if the value seems parsed already
   * @param {string} value - date in yyyy-MM-dd format.
   */
  const handleChange = useCallback(
    (value: string) => {
      if (frequency === "MONTHLY") {
        const targetDate = new Date();
        const currentDate = parse(value, "yyyy-MM-dd", new Date());
        const date = currentDate.getDate();
        targetDate.setDate(date);
        onChange(getStartDate(format(targetDate, "yyyy-MM-dd")));
      } else if (frequency === "WEEKLY") {
        const currentDate = parse(value, "yyyy-MM-dd", new Date());
        const dayOfWeek = currentDate.getDay();
        const targetDate = new Date();
        const diff = dayOfWeek - targetDate.getDay();
        targetDate.setDate(targetDate.getDate() + diff);
        onChange(getStartDate(format(targetDate, "yyyy-MM-dd")));
      }
    },
    [onChange, getStartDate, frequency]
  );

  /**
   * When firstOrderToday changes, the min start date changes,
   * there by updating getStartDate method.
   * When this happens, we need to reupdate the start date.
   */
  useEffect(() => {
    if (value) handleChange(value);
  }, [value, handleChange]);

  if (frequency === "MONTHLY")
    return (
      <Select
        value={dayNo}
        onValueChange={(val) => {
          handleChange(format(new Date().setDate(Number(val)), "yyyy-MM-dd"));
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="w-full max-h-50 overflow-y-auto">
          {days.map((day) => (
            <SelectItem key={day} value={day.toString()}>
              {day}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );

  if (frequency === "DAILY") {
    return (
      <DatePicker
        onChange={onChange}
        value={value}
        disabled={(date: Date) => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return date < (minDate ?? today);
        }}
        yearRange={{
          from: new Date().getFullYear(),
          to: new Date().getFullYear() + 10,
        }}
      />
    );
  }

  if (frequency === "WEEKLY") {
    return (
      <Select
        value={value ? new Date(value).getDay().toString() : ""}
        onValueChange={(val) => {
          const diff = parseInt(val, 10) - new Date().getDay();
          const selectedDate = new Date();
          selectedDate.setDate(selectedDate.getDate() + diff);
          handleChange(format(selectedDate, "yyyy-MM-dd"));
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="w-full max-h-50 overflow-y-auto">
          {WEEK_DAYS.map((day) => (
            <SelectItem
              disabled={!isEmpty(days) && !days.includes(day.value)}
              key={day.label}
              value={day.value.toString()}
            >
              {day.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
}
